// Настройки установки, которые правит администратор клиента в интерфейсе (005: FR-014, research,
// Р11; принципы V, VIII). Хранятся в settings.values с историей; обработчики — дорожка S2.
// 009: корпоративные домены — тоже настройка установки (FR-001в 001; принцип VIII).
import { z } from '../kernel/zod.ts';
import { PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { isEmailAddress } from '../kernel/email.ts';
import { ExternalAccessModeSchema } from '../access/email-policy.ts';

export const settingKeys = [
  'protocols.smartPaste.enabled',
  'protocols.smartPaste.jevEnabled',
  'protocols.smartPaste.deepseekEnabled',
  'protocols.smartPaste.threshold',
  'protocols.smartPaste.margin',
  'protocols.smartPaste.samplesPerColumn',
  'protocols.smartPaste.sampleChars',
  'protocols.smartPaste.columnPayloadChars',
  'protocols.smartPaste.columnDeadlineMs',
  'protocols.smartPaste.jevTimeoutMs',
  'protocols.smartPaste.deepseekColumnTimeoutMs',
  'protocols.smartPaste.maxProviderCalls',
  'protocols.smartPaste.concurrentAnalyses',
  'protocols.smartPaste.startsPerMinute',
  'protocols.smartPaste.monthlyProviderCalls',
  'protocols.smartPaste.resultTtlSeconds',
  'protocols.smartPaste.decisionRetentionDays',

  'mail.externalWithoutConfirmation',
  'directory.pairThreshold',
  'directory.kindThreshold',
  'directory.kindMonthlyLimit',
  'company.domains',
  'company.externalAccess',
  'company.externalDomains',
  'protocols.onboardingThreshold',
  'protocols.archiveSheetWords',
  'protocols.archiveConfirmThreshold',
  // 033: режим установки и список песочницы (FR-001, FR-002).
  'mailing.mode',
  'mailing.sandbox',
  // 031: календарь рабочих дней и напоминание о сроке (FR-009, FR-029).
  'calendar.holidays',
  'calendar.workdays',
  'mailing.reminderDays',
] as const;
export type SettingKey = (typeof settingKeys)[number];
export const SettingKeySchema = z.enum(settingKeys);

export const settingSections = ['mail', 'directory', 'company', 'protocols', 'mailing'] as const;
export type SettingSection = (typeof settingSections)[number];

export const settingSectionLabels = {
  mail: 'Почта',
  directory: 'Справочник',
  company: 'Компания',
  protocols: 'Протоколы',
  mailing: 'Рассылка',
} as const satisfies Record<SettingSection, string>;

/** Порог мастера подключения протокола (010, FR-017; research, Р6). */
export const ONBOARDING_THRESHOLD_MIN = 0.5;
export const ONBOARDING_THRESHOLD_MAX = 1;

/** Слова названия архивного листа (010, FR-045): 1–10 слов по 1–40 знаков, без повторов. */
export const ArchiveSheetWordsSchema = z
  .array(z.string().trim().min(1).max(40))
  .min(1)
  .max(10)
  .refine((words) => new Set(words.map((w) => w.toLocaleLowerCase('ru'))).size === words.length);

/**
 * Порог окна подтверждения архива ветви (012: FR-029, research, Р17; решение 14; принцип VIII):
 * от стольких строк ветви вместе со скрытыми потомками архив спрашивает подтверждение.
 */
export const ARCHIVE_CONFIRM_THRESHOLD_MIN = 2;
export const ARCHIVE_CONFIRM_THRESHOLD_MAX = 1000;

/** Корпоративных доменов — не больше (009, FR-004). */
export const COMPANY_DOMAINS_MAX = 20;

/** Домен так, как его хранит сервис: «  @Example.ORG » → «example.org». */
export function normalizeDomain(text: string): string {
  return text.trim().replace(/^@/, '').toLowerCase();
}

/** Части домена — буквы, цифры и дефис не по краям; последняя часть — как у адреса почты. */
const DOMAIN = /^(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?\.)+[\p{L}\p{N}-]+$/u;

/** Домен годен, если он из частей через точку и адрес на нём проходит проверку почты (FR-004). */
export function isDomain(text: string): boolean {
  return DOMAIN.test(text) && isEmailAddress(`ivanov@${text}`);
}

/** Домен в списке: уже приведён (normalizeDomain) и годен (isDomain). */
const DomainItemSchema = z
  .string()
  .refine((value) => value === normalizeDomain(value) && isDomain(value));

/** Список корпоративных доменов: в нижнем регистре, без повторов, от 1 до 20. */
export const CompanyDomainsSchema = z
  .array(DomainItemSchema)
  .min(1)
  .max(COMPANY_DOMAINS_MAX)
  .refine((domains) => new Set(domains).size === domains.length);

/** Доменов внешнего доступа — не больше (022, research Р1); как корпоративных. */
export const EXTERNAL_DOMAINS_MAX = 20;

/**
 * Домены внешнего доступа (022: research, Р1): та же форма, что у корпоративных, от 0 до 20.
 * Домен может быть и в корпоративных — такой адрес считается сотрудником (EXT-08).
 */
export const ExternalDomainsSchema = z
  .array(DomainItemSchema)
  .max(EXTERNAL_DOMAINS_MAX)
  .refine((domains) => new Set(domains).size === domains.length);

/**
 * Список песочницы (033: FR-002; research Р3): адреса, на которые уходят письма о пунктах, пока
 * установка не в боевом режиме; 0–50, без повторов без учёта регистра.
 */
export const SANDBOX_LIST_MAX = 50;
export const SandboxAddressesSchema = z
  .array(z.string().trim().refine(isEmailAddress))
  .max(SANDBOX_LIST_MAX)
  .refine((addresses) => new Set(addresses.map((a) => a.toLowerCase())).size === addresses.length);

/**
 * Отметки календаря рабочих дней (031: FR-009; research Р4) поверх производственного календаря РФ:
 * даты «ГГГГ-ММ-ДД», 0–100, без повторов. Дата в обоих списках — рабочая.
 */
export const CALENDAR_DAYS_MAX = 100;
export const CalendarDatesSchema = z
  .array(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/))
  .max(CALENDAR_DAYS_MAX)
  .refine((dates) => new Set(dates).size === dates.length);

export const PAIR_THRESHOLD_MIN = 0.3;
export const PAIR_THRESHOLD_MAX = 0.9;

/** Порог вида «организация» по оценке ИИ (005-3, FR-008). */
export const KIND_THRESHOLD_MIN = 0.5;
export const KIND_THRESHOLD_MAX = 0.99;
/** Лимит вопросов ИИ о виде в месяц (005-3, FR-018); 0 — вопрос выключен. */
export const KIND_MONTHLY_LIMIT_MAX = 100_000;

/**
 * Каталог настроек: раздел экрана, подпись, пояснение, схема значения, значение по умолчанию.
 * Значение вне схемы — SET-001. confirm — изменение через окно подтверждения.
 */
export const settingCatalog = {
  'protocols.smartPaste.enabled': {
    section: 'protocols',
    label: 'Подбирать колонки',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.boolean(),
    default: false,
    confirm: false,
  },
  'protocols.smartPaste.jevEnabled': {
    section: 'protocols',
    label: 'Классификатор JEV',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.boolean(),
    default: true,
    confirm: false,
  },
  'protocols.smartPaste.deepseekEnabled': {
    section: 'protocols',
    label: 'Подключить DeepSeek',
    description: 'Внешняя обработка только замаскированных данных, ADR-0037',
    value: z.boolean(),
    default: false,
    confirm: true,
  },
  'protocols.smartPaste.threshold': {
    section: 'protocols',
    label: 'Порог уверенности',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().min(0.5).max(1),
    default: 0.9,
    confirm: false,
  },
  'protocols.smartPaste.margin': {
    section: 'protocols',
    label: 'Разница оценок',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().min(0).max(0.5),
    default: 0.15,
    confirm: false,
  },
  'protocols.smartPaste.samplesPerColumn': {
    section: 'protocols',
    label: 'Примеров в колонке',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1).max(12),
    default: 12,
    confirm: false,
  },
  'protocols.smartPaste.sampleChars': {
    section: 'protocols',
    label: 'Знаков в примере',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(40).max(160),
    default: 160,
    confirm: false,
  },
  'protocols.smartPaste.columnPayloadChars': {
    section: 'protocols',
    label: 'Знаков для подбора колонок',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1000).max(24000),
    default: 24000,
    confirm: false,
  },
  'protocols.smartPaste.columnDeadlineMs': {
    section: 'protocols',
    label: 'Ожидание подбора, мс',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1000).max(20000),
    default: 20000,
    confirm: false,
  },
  'protocols.smartPaste.jevTimeoutMs': {
    section: 'protocols',
    label: 'Ожидание JEV, мс',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(500).max(5000),
    default: 3000,
    confirm: false,
  },
  'protocols.smartPaste.deepseekColumnTimeoutMs': {
    section: 'protocols',
    label: 'Ожидание DeepSeek для колонок, мс',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(500).max(8000),
    default: 8000,
    confirm: false,
  },
  'protocols.smartPaste.maxProviderCalls': {
    section: 'protocols',
    label: 'Попыток за анализ',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1).max(3),
    default: 3,
    confirm: false,
  },
  'protocols.smartPaste.concurrentAnalyses': {
    section: 'protocols',
    label: 'Анализов одновременно',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1).max(4),
    default: 2,
    confirm: false,
  },
  'protocols.smartPaste.startsPerMinute': {
    section: 'protocols',
    label: 'Запусков за минуту',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1).max(20),
    default: 6,
    confirm: false,
  },
  'protocols.smartPaste.monthlyProviderCalls': {
    section: 'protocols',
    label: 'Вызовов за месяц',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(0).max(100000),
    default: 2000,
    confirm: false,
  },
  'protocols.smartPaste.resultTtlSeconds': {
    section: 'protocols',
    label: 'Хранение предпросмотра, с',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(60).max(600),
    default: 600,
    confirm: false,
  },
  'protocols.smartPaste.decisionRetentionDays': {
    section: 'protocols',
    label: 'Хранение ответов моделей, дней',
    description: 'Смарт-вставка: применяется к следующим вызовам; ручная вставка доступна всегда',
    value: z.number().int().min(1).max(90),
    default: 30,
    confirm: false,
  },

  'mail.externalWithoutConfirmation': {
    section: 'mail',
    label: 'Внешняя почта без подтверждения',
    description:
      'Адреса вне доменов установки годятся для писем без отметки администратора. Включение — с подтверждением, пишется в журнал действий',
    value: z.boolean(),
    default: false,
    confirm: true,
  },
  'directory.pairThreshold': {
    section: 'directory',
    label: 'Порог пары «тот же?»',
    description:
      'Ниже порога сервис не предлагает пару. Выше — меньше вопросов, но больше риск пропустить двойника. От 0,3 до 0,9',
    value: z.number().min(PAIR_THRESHOLD_MIN).max(PAIR_THRESHOLD_MAX),
    default: 0.5,
    confirm: false,
  },
  'directory.kindThreshold': {
    section: 'directory',
    label: 'Порог вида «организация» по оценке ИИ',
    description:
      'Не ниже порога ИИ сам делает неясное упоминание организацией, с пометкой «вид — ИИ». Ниже — подсказка в «Вопросах», решает человек. От 0,5 до 0,99',
    value: z.number().min(KIND_THRESHOLD_MIN).max(KIND_THRESHOLD_MAX),
    default: 0.9,
    confirm: false,
  },
  'directory.kindMonthlyLimit': {
    section: 'directory',
    label: 'Лимит вопросов ИИ о виде в месяц',
    description:
      'Сверх лимита вопрос о виде упоминания не задаётся — неясное решает человек. 0 — вопрос выключен. Ответы из кэша в лимит не входят',
    value: z.number().int().min(0).max(KIND_MONTHLY_LIMIT_MAX),
    default: 2000,
    confirm: false,
  },
  // Умолчание — домены файла установки (kp install --domains): их подставляет сервер. Пустой
  // список здесь — только заглушка каталога, сервер его не отдаёт. Убирание — через окно.
  'company.domains': {
    section: 'company',
    label: 'Корпоративные домены',
    description:
      'Вход в сервис и почта пользователей — только на этих доменах. Письма на них уходят без подтверждения адреса',
    value: CompanyDomainsSchema,
    default: [] as string[],
    confirm: true,
  },
  // 022: внешний доступ к протоколам (research, Р1; ADR-0040). Включение — через окно; список
  // хранится и при «Выключен» и «Любые адреса»: повторное включение его возвращает.
  'company.externalAccess': {
    section: 'company',
    label: 'Внешний доступ к протоколам',
    description:
      'Владельцы протоколов могут открыть протокол людям вне компании: со всех адресов или только с доменов из списка',
    value: ExternalAccessModeSchema,
    default: 'off',
    confirm: true,
  },
  'company.externalDomains': {
    section: 'company',
    label: 'Домены внешнего доступа',
    description:
      'При «Только домены из списка» пригласить в протокол можно адрес только на этих доменах. Убрать домен — его пользователи потеряют вход',
    value: ExternalDomainsSchema,
    default: [] as string[],
    confirm: true,
  },
  // 010: мастер подключения протокола и окно загрузки (contracts/api.md 010, «Настройки установки»).
  'protocols.onboardingThreshold': {
    section: 'protocols',
    label: 'Порог мастера подключения',
    description:
      'Не ниже порога мастер ставит роль колонки сам, с пометкой «ИИ». Ниже — вопрос администратору. От 0,5 до 1',
    value: z.number().min(ONBOARDING_THRESHOLD_MIN).max(ONBOARDING_THRESHOLD_MAX),
    default: 0.9,
    confirm: false,
  },
  'protocols.archiveSheetWords': {
    section: 'protocols',
    label: 'Слова архивного листа',
    description:
      'Лист с таким названием — архив выполненных: он не становится версией протокола и листом-образцом',
    value: ArchiveSheetWordsSchema,
    default: ['выполнено', 'выполненно', 'выполненные'],
    confirm: false,
  },
  // 012: архив ветви в таблице протокола (contracts/api.md 012, «Настройки установки»).
  'protocols.archiveConfirmThreshold': {
    section: 'protocols',
    label: 'Порог подтверждения архива',
    description:
      'От стольких строк ветви, вместе со скрытыми, архив в таблице протокола спрашивает подтверждение. Меньше — сразу, с «Отменить». От 2 до 1 000',
    value: z.number().int().min(ARCHIVE_CONFIRM_THRESHOLD_MIN).max(ARCHIVE_CONFIRM_THRESHOLD_MAX),
    default: 10,
    confirm: false,
  },
  // 033 (FR-001; ADR-0045): боевой режим — через окно подтверждения, журнал settings.changed.
  'mailing.mode': {
    section: 'mailing',
    label: 'Режим рассылки',
    description:
      'Песочница: письма о пунктах уходят только на адреса из списка. Боевой режим: всем адресатам по правилам адресата',
    value: z.enum(['sandbox', 'live']),
    default: 'sandbox',
    confirm: true,
  },
  // 033 (FR-002; research Р3): в журнал действий — число адресов, без самих адресов.
  'mailing.sandbox': {
    section: 'mailing',
    label: 'Адреса песочницы',
    description:
      'До 50 адресов. Письмо уходит, только если адрес проходит и обычные правила адресата',
    value: SandboxAddressesSchema,
    default: [] as string[],
    confirm: false,
    audit: 'count',
  },
  // 031 (FR-009): отметки поверх производственного календаря РФ; журнал settings.changed.
  'calendar.holidays': {
    section: 'mailing',
    label: 'Дополнительные выходные',
    description:
      'Даты, которые не рабочие для писем и напоминаний, сверх производственного календаря РФ',
    value: CalendarDatesSchema,
    default: [] as string[],
    confirm: false,
  },
  'calendar.workdays': {
    section: 'mailing',
    label: 'Дополнительные рабочие дни',
    description: 'Даты, которые рабочие, хотя по производственному календарю РФ — выходные',
    value: CalendarDatesSchema,
    default: [] as string[],
    confirm: false,
  },
  // 031 (FR-029, Д2): напоминание о сроке за N рабочих дней.
  'mailing.reminderDays': {
    section: 'mailing',
    label: 'Напоминание о сроке, рабочих дней',
    description: 'За сколько рабочих дней до срока ответственный получает напоминание. От 1 до 5',
    value: z.number().int().min(1).max(5),
    default: 2,
    confirm: false,
  },
} as const satisfies Record<
  SettingKey,
  {
    section: SettingSection;
    label: string;
    description: string;
    value: z.ZodType;
    default: SettingValueOfAny;
    confirm: boolean;
    /** 033: в журнал действий — число элементов списка вместо значений (research Р3). */
    audit?: 'count';
  }
>;

export type SettingValue<K extends SettingKey> = z.infer<(typeof settingCatalog)[K]['value']>;
export type SettingValues = { readonly [K in SettingKey]: SettingValue<K> };

/** Значения по умолчанию — пока администратор ничего не менял (и заглушка до слияния S2). */
export const defaultSettings: SettingValues = {
  'protocols.smartPaste.enabled': settingCatalog['protocols.smartPaste.enabled'].default,
  'protocols.smartPaste.jevEnabled': settingCatalog['protocols.smartPaste.jevEnabled'].default,
  'protocols.smartPaste.deepseekEnabled':
    settingCatalog['protocols.smartPaste.deepseekEnabled'].default,
  'protocols.smartPaste.threshold': settingCatalog['protocols.smartPaste.threshold'].default,
  'protocols.smartPaste.margin': settingCatalog['protocols.smartPaste.margin'].default,
  'protocols.smartPaste.samplesPerColumn':
    settingCatalog['protocols.smartPaste.samplesPerColumn'].default,
  'protocols.smartPaste.sampleChars': settingCatalog['protocols.smartPaste.sampleChars'].default,
  'protocols.smartPaste.columnPayloadChars':
    settingCatalog['protocols.smartPaste.columnPayloadChars'].default,
  'protocols.smartPaste.columnDeadlineMs':
    settingCatalog['protocols.smartPaste.columnDeadlineMs'].default,
  'protocols.smartPaste.jevTimeoutMs': settingCatalog['protocols.smartPaste.jevTimeoutMs'].default,
  'protocols.smartPaste.deepseekColumnTimeoutMs':
    settingCatalog['protocols.smartPaste.deepseekColumnTimeoutMs'].default,
  'protocols.smartPaste.maxProviderCalls':
    settingCatalog['protocols.smartPaste.maxProviderCalls'].default,
  'protocols.smartPaste.concurrentAnalyses':
    settingCatalog['protocols.smartPaste.concurrentAnalyses'].default,
  'protocols.smartPaste.startsPerMinute':
    settingCatalog['protocols.smartPaste.startsPerMinute'].default,
  'protocols.smartPaste.monthlyProviderCalls':
    settingCatalog['protocols.smartPaste.monthlyProviderCalls'].default,
  'protocols.smartPaste.resultTtlSeconds':
    settingCatalog['protocols.smartPaste.resultTtlSeconds'].default,
  'protocols.smartPaste.decisionRetentionDays':
    settingCatalog['protocols.smartPaste.decisionRetentionDays'].default,

  'mail.externalWithoutConfirmation': settingCatalog['mail.externalWithoutConfirmation'].default,
  'directory.pairThreshold': settingCatalog['directory.pairThreshold'].default,
  'directory.kindThreshold': settingCatalog['directory.kindThreshold'].default,
  'directory.kindMonthlyLimit': settingCatalog['directory.kindMonthlyLimit'].default,
  'company.domains': settingCatalog['company.domains'].default,
  'company.externalAccess': settingCatalog['company.externalAccess'].default,
  'company.externalDomains': settingCatalog['company.externalDomains'].default,
  'protocols.onboardingThreshold': settingCatalog['protocols.onboardingThreshold'].default,
  'protocols.archiveSheetWords': settingCatalog['protocols.archiveSheetWords'].default,
  'protocols.archiveConfirmThreshold': settingCatalog['protocols.archiveConfirmThreshold'].default,
  'mailing.mode': settingCatalog['mailing.mode'].default,
  'mailing.sandbox': settingCatalog['mailing.sandbox'].default,
  'calendar.holidays': settingCatalog['calendar.holidays'].default,
  'calendar.workdays': settingCatalog['calendar.workdays'].default,
  'mailing.reminderDays': settingCatalog['mailing.reminderDays'].default,
};

/** Значение любой настройки: флаг, число, строка (022 — значение-перечисление) или список строк (009 — домены). */
export type SettingValueOfAny = boolean | number | string | string[];

export const SettingValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.string()),
]);

/** Два значения настройки равны: списки — поэлементно, по порядку. */
export function sameSettingValue(a: SettingValueOfAny, b: SettingValueOfAny): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
}

export const SettingSchema = z.object({
  key: SettingKeySchema,
  value: SettingValueSchema,
  default: SettingValueSchema,
  changedAt: TimestampSchema.nullable(),
  changedBy: PersonRefSchema.nullable(),
});
export type Setting = z.infer<typeof SettingSchema>;

export const SettingsResponseSchema = z.object({ items: z.array(SettingSchema) });

export const SettingParamsSchema = z.object({ key: SettingKeySchema });

/** Значение проверяет сервер по схеме ключа: вне её — SET-001. */
export const UpdateSettingBodySchema = z.object({ value: SettingValueSchema });

export const SettingHistorySchema = z.object({
  items: z.array(
    z.object({
      at: TimestampSchema,
      by: PersonRefSchema,
      before: SettingValueSchema,
      after: SettingValueSchema,
    }),
  ),
});
export type SettingHistory = z.infer<typeof SettingHistorySchema>;
